import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './TimePicker.scss';
import { FaClock } from 'react-icons/fa';

interface TimePickerProps {
  value: string; // format HH:MM
  onChange: (val: string) => void;
  minuteStep?: number;
  disabled?: boolean;
  ariaLabel?: string;
}

const pad = (n: number) => n.toString().padStart(2, '0');

const TimePicker: React.FC<TimePickerProps> = ({ value, onChange, minuteStep = 5, disabled, ariaLabel }) => {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const displayRef = useRef<HTMLDivElement | null>(null);
  const [panelPos, setPanelPos] = useState<{ top: number; left: number; alignRight: boolean } | null>(null);
  const [hours, setHours] = useState<string>('00');
  const [minutes, setMinutes] = useState<string>('00');

  // Parse incoming value
  useEffect(() => {
    if (/^\d{2}:\d{2}$/.test(value)) {
      const [h, m] = value.split(':');
      setHours(h);
      setMinutes(m);
    }
  }, [value]);

  const commit = useCallback((h: string, m: string) => {
    onChange(`${h}:${m}`);
  }, [onChange]);

  const toggleOpen = () => {
    if (disabled) return;
    setOpen(o => !o);
  };

  const close = () => {
    if (!open) return;
    setClosing(true);
    // Delay unmount to allow animation
    setTimeout(() => {
      setOpen(false);
      setClosing(false);
      setPanelPos(null);
    }, 160);
  };

  // Close on outside click
  useEffect(() => {
    const outsideHandler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    };
    const handleResizeScroll = () => {
      if (!open) return;
      positionPanel();
    };
    if (open) {
      document.addEventListener('mousedown', outsideHandler);
      window.addEventListener('resize', handleResizeScroll);
      window.addEventListener('scroll', handleResizeScroll, true);
      positionPanel();
    }
    return () => {
      document.removeEventListener('mousedown', outsideHandler);
      window.removeEventListener('resize', handleResizeScroll);
      window.removeEventListener('scroll', handleResizeScroll, true);
    };
  }, [open]);

  const positionPanel = () => {
    const el = displayRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const panelWidth = 260; // must match CSS
    const margin = 8;
    let left = rect.left;
    let alignRightLocal = false;
    if (left + panelWidth > window.innerWidth - margin) {
      left = Math.max(margin, rect.right - panelWidth);
      alignRightLocal = true;
    }
    const top = rect.bottom + 6; // gap
  setPanelPos({ top, left, alignRight: alignRightLocal });
  setAlignRight(alignRightLocal);
  };

  // Keyboard accessibility
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleOpen();
    } else if (e.key === 'Escape') {
      close();
    }
  };

  const handleHourClick = (h: number) => {
    const hh = pad(h);
    setHours(hh);
    commit(hh, minutes);
  };

  const handleMinuteClick = (m: number) => {
    const mm = pad(m);
    setMinutes(mm);
    commit(hours, mm);
    // Auto-close after selecting minute for faster UX
    close();
  };

  const hoursArray = Array.from({ length: 24 }, (_, i) => i);
  const minutesArray = Array.from({ length: Math.floor(60 / minuteStep) }, (_, i) => i * minuteStep);

  const panelContent = open && panelPos ? (
    <div
      className={`time-panel portal ${alignRight ? 'align-right' : ''} ${closing ? 'closing' : ''}`}
      role="dialog"
      aria-label="Panel selector de hora"
      style={{ position: 'fixed', top: panelPos.top, left: panelPos.left }}
    >
      <div className="column hours" aria-label="Horas">
        <div className="column-title">Horas</div>
        <div className="values">
          {hoursArray.map(h => {
            const hh = pad(h);
            return (
              <div
                key={hh}
                className={`val ${hh === hours ? 'active' : ''}`}
                onClick={() => handleHourClick(h)}
              >
                {hh}
              </div>
            );
          })}
        </div>
      </div>
      <div className="column minutes" aria-label="Minutos">
        <div className="column-title">Minutos</div>
        <div className="values">
          {minutesArray.map(m => {
            const mm = pad(m);
            return (
              <div
                key={mm}
                className={`val ${mm === minutes ? 'active' : ''}`}
                onClick={() => handleMinuteClick(m)}
              >
                {mm}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <div
        className={`mp-time-picker ${disabled ? 'disabled' : ''}`}
        ref={containerRef}
        data-open={open}
      >
        <div
          className="time-display"
          onClick={toggleOpen}
          onKeyDown={onKeyDown}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label={ariaLabel || 'Selector de hora'}
          aria-expanded={open}
          ref={displayRef}
        >
          <span className="time-text">{hours}:{minutes}</span>
          <FaClock className="time-icon" />
        </div>
      </div>
      {panelContent && ReactDOM.createPortal(panelContent, document.body)}
    </>
  );
};

export default TimePicker;